import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { SplitButtonMenuComponent } from './split-button-menu.component';
import { SplitButtonMenuItem } from './split-button.dto';

const STORAGE_KEY = 'splitButtonState';

@Component({
  selector: 'app-split-button',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule],
  templateUrl: './split-button.component.html',
  styleUrls: ['./split-button.component.scss'],
})
export class SplitButtonComponent implements OnInit, OnDestroy {
  @Input() mainItem: SplitButtonMenuItem;
  @Input() menuItems: SplitButtonMenuItem[] = [];
  @Input() disabled = false;
  @Input() persistState = true;
  @Input() buttonId: string;

  @Output() mainActionClick = new EventEmitter<string>();
  @Output() menuItemClick = new EventEmitter<string>();

  private dynamicOverlayService = inject(DynamicOverlayService);
  private menuSubscription: Subscription | undefined;

  isMenuOpen = signal(false);
  currentMainActionId = signal('');
  currentMainActionText = signal('');
  currentMainActionIcon = signal('');
  currentMainActionFunc = signal<() => void>(() => {});

  ngOnInit(): void {
    // Set initial state from inputs
    this.currentMainActionId.set(this.mainItem.id);
    this.currentMainActionText.set(this.mainItem.text);
    this.currentMainActionIcon.set(this.mainItem.icon);
    this.currentMainActionFunc.set(this.mainItem.action);

    // If persistence is enabled, try to load the saved state
    if (this.persistState && this.buttonId) {
      this.loadState();
    }
  }

  private loadState(): void {
    try {
      const allStates = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      const savedActionId = allStates[this.buttonId];
      if (savedActionId) {
        const savedAction = this.menuItems.find((item) => item.id === savedActionId);
        if (savedAction) {
          this.updateMainAction(savedAction);
        }
      }
    } catch (e) {
      console.error('Failed to parse split button state from localStorage', e);
    }
  }

  private saveState(actionId: string): void {
    if (!this.persistState || !this.buttonId) return;

    try {
      const allStates = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      allStates[this.buttonId] = actionId;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allStates));
    } catch (e) {
      console.error('Failed to save split button state to localStorage', e);
    }
  }

  private updateMainAction(item: SplitButtonMenuItem): void {
    this.currentMainActionText.set(item.text);
    this.currentMainActionIcon.set(item.icon);
    this.currentMainActionFunc.set(item.action || (() => {}));
    this.currentMainActionId.set(item.id);
  }

  onMainActionClick(): void {
    this.mainActionClick.emit(this.currentMainActionId());
  }

  openSplitButtonMenu(event: MouseEvent, triggerElement: HTMLElement): void {
    event.stopPropagation();
    this.isMenuOpen.set(true);

    this.menuSubscription?.unsubscribe();

    const { ref: overlayRef, componentInstance } = this.dynamicOverlayService.open(triggerElement, SplitButtonMenuComponent, {
      data: this.menuItems,
    });

    this.menuSubscription = componentInstance.itemSelected.subscribe((item: SplitButtonMenuItem) => {
      this.menuItemClick.emit(item.id); // Always emit the original event

      if (this.persistState) {
        this.updateMainAction(item);
        this.saveState(item.id);
      }

      this.dynamicOverlayService.close();
    });

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => {
        this.menuSubscription?.unsubscribe();
        this.isMenuOpen.set(false);
      });
  }

  ngOnDestroy(): void {
    this.menuSubscription?.unsubscribe();
    this.dynamicOverlayService.close();
  }
}
